import { useState } from "react";
import { toast } from "sonner";
import { Crown, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { band } from "@/components/ResultScore";
import { useAuth } from "@/context/AuthContext";
import { api, asList, enc, errMsg } from "@/lib/api";
import { getCurrentResume } from "@/lib/atsScores";
import { bestTailoredFor, getAutoPick } from "@/lib/tailoredLib";
import { noteApply } from "@/lib/streak";

export const usePickBeforeApply = (onApply) => {
  const { refreshProfile } = useAuth();
  const [pick, setPick] = useState(null);
  const start = (jobId) => {
    const best = getAutoPick() && bestTailoredFor(jobId);
    if (!best || best.file_name === getCurrentResume()) return onApply();
    setPick(best);
  };
  const useIt = async () => {
    const p = pick;
    setPick(null);
    onApply();
    noteApply(true);
    try {
      const v = asList((await api.get("/profile/resume/versions")).data, "versions").find((x) => x.name === p.file_name || x.label === p.file_name);
      if (!v) return toast.error("That tailored version isn't in your saved resumes anymore.");
      if (!v.is_current) await api.post(`/profile/resume/versions/${enc(v.version_id)}/restore`);
      await refreshProfile();
      window.dispatchEvent(new CustomEvent("rpopro:resume-replaced", { detail: {} }));
      toast.success(`Now using ${p.file_name} for this job`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const keep = () => { setPick(null); onApply(); };
  const dialog = <AutoPickDialog pick={pick} onUse={useIt} onKeep={keep} onCancel={() => setPick(null)} />;
  return { start, dialog };
};

const AutoPickDialog = ({ pick, onUse, onKeep, onCancel }) => {
  const b = pick?.score != null && band(pick.score);
  return (
    <Dialog open={!!pick} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="border-[#D4AF37]/40 bg-[#141417] sm:max-w-lg" data-testid="auto-pick-dialog">
        <DialogTitle className="flex items-center gap-2 font-display text-zinc-100"><Crown className="h-5 w-5 text-[#D4AF37]" />Use your tailored version?</DialogTitle>
        <DialogDescription className="text-zinc-400">You have a resume tailored for this job. Switch to it before you apply?</DialogDescription>
        {pick && (
          <div className="flex items-center gap-3 rounded-xl border border-[#27272A] bg-[#09090B] p-3" data-testid="auto-pick-version">
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-zinc-100">{pick.file_name}</div>
              <div className="text-[11px] text-zinc-500">Current: {getCurrentResume() || "your main resume"}</div>
            </div>
            {b && <span className={`shrink-0 rounded-lg border px-2 py-1 font-mono text-sm font-bold ${b.bg} ${b.text}`} data-testid="auto-pick-score">{pick.score}%</span>}
          </div>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onCancel} data-testid="auto-pick-cancel">Cancel</Button>
          <Button variant="outline" onClick={onKeep} data-testid="auto-pick-keep">Keep current & apply</Button>
          <Button onClick={onUse} data-testid="auto-pick-use"><ExternalLink className="h-4 w-4" />Use it & apply</Button>
        </div>
        <p className="text-[11px] text-zinc-600">Turn this off any time in Profile → ATS automation.</p>
      </DialogContent>
    </Dialog>
  );
};
